// employeeAccess.js
// Tilgangsnivå og roller for den valgte ansatte (satt i NavBar ved ansattvalg, §38). Ikke ekte
// autentisering — kundedata har delt butikk-pålogging, ikke individuell ansattpålogging — men
// lar UI skjule ledere-only-funksjoner for andre ansatte uten å måtte bygge om hele innloggingen.
//
// tilgang: «ansatt» eller «leder». roller: ekstra roller i tillegg (Employee.ROLLER i backend),
// som bare leder gir under Ansatte.
import { useEffect, useState } from 'react';
import Cookies from 'js-cookie';

// Ekstra roller leder kan gi. Samme id-er som ROLLER i src/model/Employee.js.
export const ROLLER = [
  { id: 'mekaniker', navn: 'Mekaniker', forklaring: 'Ser og endrer flere detaljer på servicer.' },
  { id: 'klubbansvarlig', navn: 'Klubbansvarlig', forklaring: 'Skriver i klubbportalen: klubber, innlegg, trenere og innkjøpsansvarlige.' },
];

// Valgene for «Butikk» under Ansatte. Røyken Sykkelservice (verkstedet) betjener verkstedene i
// begge butikkene, så de ansatte der vises i begge, som «Begge butikker».
export const BUTIKK_VALG = ['Sport1 Røyken', 'Sport1 Slemmestad', 'Begge butikker', 'Røyken Sykkelservice'];
const I_BEGGE = ['Begge butikker', 'Røyken Sykkelservice'];

// «På pause» (Anders 03.11, pauseFra i src/model/Employee.js): har ikke sluttet, men får ikke
// nyheter, klubbinfo eller SMS nå. Kan fortsatt velge seg selv i menyen («(pause)»).
export const paPause = (ansatt) => !!ansatt?.pauseFra;
// Kommer ikke med i «Velg alle» og meldinger til alle: på pause eller «Ikke aktiv» (butikk «Ingen»).
// Samme regel som Employee.utenMeldinger i backend.
export const utenMeldinger = (ansatt) => paPause(ansatt) || ansatt?.butikk === 'Ingen';

// Om den ansatte skal kunne velges i butikken (menylinjen, chat i verkstedordren).
export const jobberI = (ansatt, butikk) => I_BEGGE.includes(ansatt?.butikk) || ansatt?.butikk === butikk;

export const getSelectedEmployeeTilgang = () => Cookies.get('selectedEmployeeTilgang') || '';
export const getSelectedEmployeeRoller = () =>
  (Cookies.get('selectedEmployeeRoller') || '').split(',').filter(Boolean);

export const isLeder = () => getSelectedEmployeeTilgang() === 'leder';
export const erMekaniker = () => getSelectedEmployeeRoller().includes('mekaniker');
// Tjenester, priser og kapasitet (Tjenester og priser): bare leder og mekaniker (Anders, 28.10).
// Backenden sjekker det samme (src/routes/tjenestekatalog.js).
export const kanEndrePriser = () => isLeder() || erMekaniker();
// Klubbportalen (/klubber): alle leser, leder og klubbansvarlig skriver (vinnsel, 02.11).
// Backenden sjekker det samme (src/routes/klubbportal.js).
export const kanSkriveKlubb = () => isLeder() || getSelectedEmployeeRoller().includes('klubbansvarlig');

// Lagrer tilgang og roller for den valgte ansatte. Gir true hvis noe ble endret.
export function lagreTilgang(ansatt) {
  const tilgang = ansatt?.tilgang || 'ansatt';
  const roller = (ansatt?.roller || []).join(',');
  const endret = tilgang !== getSelectedEmployeeTilgang() || roller !== (Cookies.get('selectedEmployeeRoller') || '');
  Cookies.set('selectedEmployeeTilgang', tilgang);
  if (roller) Cookies.set('selectedEmployeeRoller', roller);
  else Cookies.remove('selectedEmployeeRoller');
  return endret;
}

export function glemTilgang() {
  Cookies.remove('selectedEmployeeTilgang');
  Cookies.remove('selectedEmployeeRoller');
}

// For komponenter: verdien av sjekk(), oppdatert når NavBar melder bytte av ansatt.
function useTilgang(sjekk) {
  const [verdi, setVerdi] = useState(sjekk);
  useEffect(() => {
    const oppdater = () => setVerdi(sjekk());
    window.addEventListener('employeeUpdated', oppdater);
    return () => window.removeEventListener('employeeUpdated', oppdater);
  }, [sjekk]);
  return verdi;
}

export const useErLeder = () => useTilgang(isLeder);
export const useErMekaniker = () => useTilgang(erMekaniker);
export const useKanEndrePriser = () => useTilgang(kanEndrePriser);
export const useKanSkriveKlubb = () => useTilgang(kanSkriveKlubb);
